import { isTag, isText, type ChildNode, type Element } from 'domhandler';
import {
  classifyMarkupFragment,
  getAttribute,
  getNodeText,
  getTagChildren,
  parseDocrefDocument,
  parseHtmlDocument,
} from './document-reference-dom';
import type {
  DocumentReferenceCallout,
  DocumentReferenceCaption,
  DocumentReferenceStructuredBlock,
  DocumentReferenceStructuredContentPayload,
  DocumentReferenceStructuredItem,
  DocumentReferenceStructuredSection,
  DocumentReferenceTableRow,
} from './document-reference-types';
import { normalisePreviewTextArtifacts } from '../signedUrlPreviewUtils';

export type DocumentReferencePreviewContent =
  | {
      kind: 'structured';
      content: DocumentReferenceStructuredContentPayload;
    }
  | {
      kind: 'text';
      content: string;
    };

const HTML_ENTITY_PATTERN = /&(?:[A-Za-z][\w]+|#\d+|#x[\dA-Fa-f]+);/;
const MAX_PLAIN_TEXT_PARSE_DEPTH = 3;
const BLOCK_TEXT_TAG_NAMES = new Set(['div', 'p', 'paragraph', 'table', 'tr']);
const CELL_TEXT_TAG_NAMES = new Set(['td', 'th']);
const SKIPPED_TEXT_TAG_NAMES = new Set(['script', 'style']);

export const parseDocumentReferenceMarkup = (
  raw: string | undefined,
): DocumentReferenceStructuredContentPayload | undefined => {
  const trimmed = stripXmlDeclaration(raw?.trim() ?? '');
  if (!trimmed) return undefined;

  const classification = classifyMarkupFragment(trimmed);
  if (!classification.isDocref) return undefined;
  if (!isWellFormedXmlFragment(trimmed)) return undefined;

  try {
    const document = parseDocrefDocument(trimmed);
    const listNode = getTagChildren(document).find(
      (node) => node.name.toLowerCase() === 'list',
    );
    const items = listNode
      ? parseListItems(listNode)
      : parseLooseRootItems(document.children);
    if (items.length === 0) return undefined;

    return {
      items,
      footnotes: parseGlobalFootnotes(listNode?.children ?? document.children),
    };
  } catch {
    return undefined;
  }
};

export const getDocumentReferencePreviewContent = (
  raw: string,
): DocumentReferencePreviewContent => {
  const decodedText = normalisePreviewTextArtifacts(decodeIfEncodedMarkup(raw));
  const markupCandidate = stripXmlDeclaration(decodedText.trimStart());
  const structured = parseDocumentReferenceMarkup(markupCandidate);

  if (structured) {
    return { kind: 'structured', content: structured };
  }

  return {
    kind: 'text',
    content: getPlainTextContent(decodedText),
  };
};

const parseListItems = (
  listNode: Element,
): DocumentReferenceStructuredItem[] => {
  const styleCode = getAttribute(listNode, 'styleCode')?.toLowerCase();
  if (styleCode && styleCode !== 'xtoc') return [];

  return getTagChildren(listNode)
    .filter((entry) => entry.name.toLowerCase() === 'item')
    .map((entry) => parseItem(entry))
    .filter((item): item is DocumentReferenceStructuredItem => !!item);
};

const parseLooseRootItems = (
  children: ReadonlyArray<ChildNode>,
): DocumentReferenceStructuredItem[] => {
  const looseChildren = children.filter((child) => {
    if (isText(child)) return Boolean(child.data.trim());
    return !(isTag(child) && child.name.toLowerCase() === 'footnote');
  });

  if (looseChildren.length === 0) return [];
  if (
    looseChildren.some(
      (child) =>
        isTag(child) &&
        !['content', 'paragraph', 'table'].includes(child.name.toLowerCase()),
    )
  ) {
    return [];
  }

  const syntheticContent = createSyntheticElement('content', looseChildren);
  const item = parseItem(syntheticContent);
  return item ? [item] : [];
};

const parseItem = (
  node: Element,
): DocumentReferenceStructuredItem | undefined => {
  const tagChildren = getTagChildren(node);
  const captionNode = tagChildren.find(
    (child) => child.name.toLowerCase() === 'caption',
  );
  const contentNodes = tagChildren.filter(
    (child) => child.name.toLowerCase() === 'content',
  );
  const footnotes = tagChildren
    .filter((child) => child.name.toLowerCase() === 'footnote')
    .map(parseFootnote)
    .filter((footnote): footnote is DocumentReferenceCallout => !!footnote);

  const blocks = contentNodes
    .map(parseContentBlock)
    .filter((block): block is DocumentReferenceStructuredBlock => !!block);

  if (blocks.length === 0 && footnotes.length === 0) return undefined;

  return {
    caption: parseCaption(
      captionNode
        ? normaliseWhitespace(getNodeText(captionNode.children))
        : undefined,
    ),
    blocks,
    footnotes,
  };
};

const parseContentBlock = (
  node: Element,
): DocumentReferenceStructuredBlock | undefined => {
  const sections: DocumentReferenceStructuredSection[] = [];
  const segments: Array<
    | { kind: 'text'; text: string }
    | {
        kind: 'callout';
        text: string;
        styleCode?: string;
        id?: string;
      }
  > = [];
  let currentSection: DocumentReferenceStructuredSection | undefined;

  const getOrCreateSection = () => {
    if (!currentSection) {
      currentSection = { contents: [] };
      sections.push(currentSection);
    }
    return currentSection;
  };

  const appendNode = (child: ChildNode) => {
    if (!isTag(child)) {
      const text = normaliseWhitespace(getNodeText(child));
      if (text) {
        segments.push({ kind: 'text', text });
        getOrCreateSection().contents.push({ kind: 'paragraph', text });
      }
      return;
    }

    const name = child.name.toLowerCase();
    const styleCode = getAttribute(child, 'styleCode');
    if (name === 'content' && styleCode === 'xLabel') {
      const text = normaliseWhitespace(getNodeText(child.children));
      if (text) {
        segments.push({
          kind: 'callout',
          text,
          styleCode,
          id: getAttribute(child, 'ID'),
        });
      }
      return;
    }

    if (name === 'footnote') {
      const footnote = parseFootnote(child);
      if (footnote) {
        segments.push({
          kind: 'callout',
          text: footnote.text,
          styleCode: footnote.styleCode,
          id: footnote.id,
        });
      }
      return;
    }

    if (name === 'paragraph') {
      const text = normaliseWhitespace(getNodeText(child.children));
      if (text) segments.push({ kind: 'text', text });
      applyParagraph(
        child,
        sections,
        () => currentSection,
        (section) => {
          currentSection = section;
        },
      );
      return;
    }

    if (name === 'table') {
      const rows = parseTableRows(child);
      if (rows.length > 0) {
        segments.push({ kind: 'text', text: '__structured__' });
        getOrCreateSection().contents.push({ kind: 'table', rows });
      }
      return;
    }

    if (name === 'list') {
      const items = getTagChildren(child)
        .filter((entry) => entry.name.toLowerCase() === 'item')
        .map((entry) => normaliseWhitespace(getNodeText(entry.children)))
        .filter(Boolean);
      if (items.length > 0) {
        segments.push({ kind: 'text', text: '__structured__' });
        getOrCreateSection().contents.push({ kind: 'list', items });
        return;
      }
    }

    if (name === 'content' || name === 'div' || name === 'span') {
      getMeaningfulChildren(child.children).forEach(appendNode);
    }
  };

  getMeaningfulChildren(node.children).forEach(appendNode);

  const callouts = segments.flatMap((segment, index) => {
    if (segment.kind !== 'callout') return [];

    return [
      {
        text: segment.text,
        styleCode: segment.styleCode,
        id: segment.id,
        placement: getCalloutPlacement(segments, index),
      },
    ];
  });

  if (sections.length === 0 && callouts.length === 0) return undefined;

  return {
    id: getAttribute(node, 'ID'),
    styleCode: getAttribute(node, 'styleCode'),
    callouts,
    sections: sections.filter((section) => section.contents.length > 0),
  };
};

const getCalloutPlacement = (
  segments: ReadonlyArray<
    | { kind: 'text'; text: string }
    | { kind: 'callout'; text: string; styleCode?: string; id?: string }
  >,
  index: number,
): DocumentReferenceCallout['placement'] => {
  const hasTextBefore = segments
    .slice(0, index)
    .some((segment) => segment.kind === 'text');
  const hasTextAfter = segments
    .slice(index + 1)
    .some((segment) => segment.kind === 'text');

  if (!hasTextBefore && hasTextAfter) return 'leading';
  if (hasTextBefore && !hasTextAfter) return 'trailing';
  return 'inline';
};

const applyParagraph = (
  node: Element,
  sections: DocumentReferenceStructuredSection[],
  getCurrentSection: () => DocumentReferenceStructuredSection | undefined,
  setCurrentSection: (section: DocumentReferenceStructuredSection) => void,
) => {
  const text = normaliseWhitespace(getNodeText(node.children));
  if (!text) return;

  const keyValue = parseKeyValue(text);
  if (keyValue) {
    const currentSection = getCurrentSection() ?? { contents: [] };
    setCurrentSection(currentSection);
    if (!sections.includes(currentSection)) sections.push(currentSection);
    currentSection.contents.push({ kind: 'keyValue', entries: [keyValue] });
    return;
  }

  const currentSection = getCurrentSection();
  if (!currentSection || currentSection.contents.length > 0) {
    const nextSection: DocumentReferenceStructuredSection = {
      heading: text,
      headingSource: 'structural',
      contents: [],
    };
    sections.push(nextSection);
    setCurrentSection(nextSection);
    return;
  }

  currentSection.contents.push({ kind: 'paragraph', text });
};

const parseTableRows = (table: Element): DocumentReferenceTableRow[] =>
  getTagChildren(table)
    .filter((child) => child.name.toLowerCase() === 'tr')
    .flatMap((row) => {
      const cells = getTagChildren(row)
        .filter((cell) => ['td', 'th'].includes(cell.name.toLowerCase()))
        .map((cell) => normaliseWhitespace(getNodeText(cell.children)));
      if (cells.length >= 3) {
        return [{ test: cells[0], unit: cells[1], value: cells[2] }];
      }
      if (cells.length === 2) {
        return [{ test: cells[0], value: cells[1] }];
      }
      return [];
    });

const parseKeyValue = (
  text: string,
): { key: string; value?: string } | undefined => {
  const colonIndex = text.indexOf(':');
  if (colonIndex <= 0) return undefined;

  const key = text.slice(0, colonIndex).trim();
  const value = text.slice(colonIndex + 1).trim();
  if (!key) return undefined;
  if (key.length > 40) return undefined;
  if (/[!?]/.test(key)) return undefined;
  if (/[a-z]\.\s+[a-z]/i.test(key)) return undefined;
  if (/(?:^|\s)(?:[01]?\d|2[0-3])$/.test(key) && /^[0-5]\d\b/.test(value)) {
    return undefined;
  }

  return {
    key,
    value: value || undefined,
  };
};

const parseCaption = (raw: string | undefined): DocumentReferenceCaption => {
  const cleaned = normaliseWhitespace(raw ?? '');
  if (!cleaned) return {};

  const caption: DocumentReferenceCaption = { raw: cleaned };
  const dashIndex = cleaned.indexOf(' - ');
  const left = dashIndex >= 0 ? cleaned.slice(0, dashIndex) : cleaned;
  const timestampRaw =
    dashIndex >= 0 ? cleaned.slice(dashIndex + 3).trim() : undefined;

  const authorParts = left.split(',');
  caption.author = authorParts.shift()?.trim();
  const credentialSegment = authorParts.join(',').trim();
  if (credentialSegment) caption.credentials = credentialSegment;

  if (timestampRaw) {
    caption.timestampRaw = timestampRaw;
    caption.timestampIso = parseTimestamp(timestampRaw);
  }

  return caption;
};

const parseTimestamp = (raw: string): string | undefined => {
  const parsed = new Date(raw.replace(/\b(ET|EST|EDT)\b/i, ''));
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
};

const parseGlobalFootnotes = (
  children: ReadonlyArray<ChildNode>,
): DocumentReferenceCallout[] =>
  children
    .filter(
      (node): node is Element =>
        isTag(node) && node.name.toLowerCase() === 'footnote',
    )
    .map(parseFootnote)
    .filter((footnote): footnote is DocumentReferenceCallout => !!footnote);

const parseFootnote = (node: Element): DocumentReferenceCallout | undefined => {
  const text = normaliseWhitespace(getNodeText(node.children));
  if (!text) return undefined;

  return {
    text,
    placement: 'trailing',
    styleCode: getAttribute(node, 'styleCode'),
    id: getAttribute(node, 'ID'),
  };
};

const getMeaningfulChildren = (
  children: ReadonlyArray<ChildNode>,
): ChildNode[] =>
  children.filter((child) => !isText(child) || Boolean(child.data.trim()));

const createSyntheticElement = (
  name: string,
  children: ReadonlyArray<ChildNode>,
): Element =>
  ({
    type: 'tag',
    name,
    attribs: {},
    children: [...children],
  }) as Element;

const decodeIfEncodedMarkup = (text: string): string => {
  if (!HTML_ENTITY_PATTERN.test(text) || looksLikeMarkup(text)) return text;

  const decoded = decodeHtmlEntities(text);
  return looksLikeMarkup(decoded) ? decoded : text;
};

const getPlainTextContent = (text: string): string => {
  if (!looksLikeMarkup(text) && !HTML_ENTITY_PATTERN.test(text)) {
    return text.trim();
  }

  let current = text.trim();

  for (let depth = 0; depth < MAX_PLAIN_TEXT_PARSE_DEPTH; depth += 1) {
    const decoded = decodeIfEncodedMarkup(current);
    const document = parseHtmlDocument(decoded);
    const next = normaliseWhitespace(getSafeNodeText(document.children));

    if (!next) return current;
    if (next === current) return next;
    if (!looksLikeMarkup(next) && !HTML_ENTITY_PATTERN.test(next)) return next;

    current = next;
  }

  return current.trim();
};

const decodeHtmlEntities = (value: string): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

const looksLikeMarkup = (value: string): boolean =>
  /<\/?[A-Za-z][^>]*>/.test(value);

const stripXmlDeclaration = (value: string): string =>
  value.replace(/^<\?xml[\s\S]*?\?>\s*/i, '');

const isWellFormedXmlFragment = (value: string): boolean => {
  if (typeof DOMParser === 'undefined') return true;

  const document = new DOMParser().parseFromString(
    `<docref-root>${value}</docref-root>`,
    'text/xml',
  );
  return document.querySelector('parsererror') === null;
};

const getSafeNodeText = (nodes: ReadonlyArray<ChildNode>): string => {
  const parts: string[] = [];

  nodes.forEach((node) => {
    if (isText(node)) {
      parts.push(node.data);
      return;
    }

    if (!isTag(node)) return;
    const tagName = node.name.toLowerCase();
    if (SKIPPED_TEXT_TAG_NAMES.has(tagName)) return;

    if (tagName === 'br') {
      parts.push('\n');
      return;
    }

    const childText = getSafeNodeText(node.children);
    if (!childText) return;

    if (CELL_TEXT_TAG_NAMES.has(tagName)) {
      parts.push(`${childText}\n`);
      return;
    }

    if (BLOCK_TEXT_TAG_NAMES.has(tagName)) {
      parts.push(`\n${childText}\n`);
      return;
    }

    parts.push(childText);
  });

  return parts.join('');
};

const normaliseWhitespace = (value: string): string =>
  normalisePreviewTextArtifacts(
    value
      .replace(/\r\n/g, '\n')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .join('\n')
      .trim(),
  );
